import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { faReply } from "@fortawesome/pro-solid-svg-icons/faReply";
import { faReplyAll } from "@fortawesome/pro-solid-svg-icons/faReplyAll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import Space from "antd/es/space";
import Tooltip from "antd/es/tooltip";
import { uniqBy } from "lodash-es";
import { useIntl } from "react-intl";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailRecipientAddress from "@mapmycustomers/shared/types/email/EmailRecipientAddress";
import { ChildLayout } from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button } from "@mapmycustomers/ui";

import ActivityCreationModal from "@app/component/email/creation/ActivityCreationModal";
import CreateEmailBody from "@app/component/email/creation/CreateEmailBody";
import CreateEmailContext from "@app/component/email/creation/CreateEmailContext";
import emailCreationMessages from "@app/component/email/creation/messages";
import PreviewModal from "@app/component/email/creation/PreviewModal";
import EmailTemplateButton from "@app/component/email/creation/Template/TemplateButton";
import Checkbox from "@app/component/input/Checkbox";
import { getEmailActivityType } from "@app/store/activity";
import {
  ActivityCreationPayloadWithCustomFields,
  EmailCreationPayload,
} from "@app/store/api/ApiService";
import { doesNeedToOpenActivityFormAfterSendingEmail } from "@app/store/email";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { activityLayoutModel } from "@app/util/layout/impl";

import { loadSendEmail } from "../store/actions";
import { getEmailThreadsEntity } from "../store/selectors";
import normalizeEmail from "../util/normalizeEmail";
import useIsCurrentUserEmail from "../util/useIsCurrentUserEmail";

import messages from "./messages";
import styles from "./NewEmail.module.scss";

type NewEmailProps = {
  onClose: (send: boolean) => void;
  onLoadSendEmail: typeof loadSendEmail.request;
  repliedAll: boolean;
  replyEmail: Email;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const NewEmail: React.FC<NewEmailProps> = ({
  currentUserId,
  emailActivityType,
  entity,
  onClose,
  onLoadSendEmail,
  openActivityModal,
  repliedAll,
  replyEmail,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    attachedFiles,
    attachedFilesSizeLimitReached,
    body,
    bodyLimitReached,
    emailFilled,
    send,
    sending,
    setSending,
    setSubject,
    subject,
  } = useNonNullableContext(CreateEmailContext);

  const bottomRef = useRef<HTMLDivElement>(null);
  const [formLayout, setFormLayout] = useState(() => activityLayoutModel.defaultFormLayout!);

  const isCurrentUserEmail = useIsCurrentUserEmail();

  const [activityCreationModalOpen, showActivityCreationModal, hideActivityCreationModal] =
    useBoolean();
  const [createActivity, setCreateActivity] = useState<boolean>(false);

  const emailSendByCurrentUser = isCurrentUserEmail(replyEmail.from[0].email);

  const emailRecipients = useMemo(() => {
    let reply: EmailRecipientAddress[];
    let cc: EmailRecipientAddress[] = [];

    const emailSender = replyEmail.from[0];

    if (repliedAll) {
      reply = emailSendByCurrentUser
        ? replyEmail.recipientAddress
        : [emailSender, ...replyEmail.recipientAddress];

      cc = replyEmail.cc ?? [];
    } else {
      reply = emailSendByCurrentUser ? replyEmail.recipientAddress : [emailSender];
    }

    let all = uniqBy([...reply, ...cc], (emailRecipient) => emailRecipient.email);
    if (all.length > 1) {
      all = all.filter((emailRecipient) => !isCurrentUserEmail(emailRecipient.email));
    }

    return {
      all,
      cc,
      reply,
    };
  }, [emailSendByCurrentUser, isCurrentUserEmail, repliedAll, replyEmail]);

  const emailRecipientsHeader = useMemo(() => {
    return emailRecipients.all
      .map((recipient) =>
        recipient.name ? `${recipient.name} (${recipient.email})` : recipient.email
      )
      .join(", ");
  }, [emailRecipients]);

  const sendEmailDisabled =
    sending || bodyLimitReached || !emailFilled || attachedFilesSizeLimitReached;

  const sendEmailTooltip = useMemo(() => {
    if (attachedFilesSizeLimitReached) {
      return intl.formatMessage(emailCreationMessages.filesSizeLimitReached);
    }

    if (bodyLimitReached) {
      return intl.formatMessage(emailCreationMessages.emailBodyLimitReached);
    }

    if (!emailFilled) {
      return intl.formatMessage(messages.emailIsNotFilled);
    }

    return null;
  }, [intl, attachedFilesSizeLimitReached, bodyLimitReached, emailFilled]);

  useEffect(() => {
    setCreateActivity(false);
  }, [replyEmail]);

  const displaySuccessNotification = useCallback(() => {
    notification.success({
      message: intl.formatMessage(messages.emailSentSuccessNotificationMessage),
    });
  }, [intl]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [bottomRef]);

  useEffect(() => {
    setSubject(replyEmail.subject);
  }, [replyEmail, setSubject]);

  const handleSend = useCallback(
    (
      crmActivity?: ActivityCreationPayloadWithCustomFields,
      customFieldsValidationCallback?: (customFieldErrors: CustomFieldError[]) => void
    ) => {
      let childLayout: ChildLayout | undefined;
      if (crmActivity) {
        childLayout = formLayout.childLayouts?.find(
          ({ crmActivityType }) => crmActivityType?.name === DefaultActivityTypeName.EMAIL
        );
      }

      setSending(true);

      const email: EmailCreationPayload = {
        body: body,
        childLayoutId: childLayout?.id,
        entityType: entity.entity,
        instant: true,
        isHtml: true,
        layoutId: formLayout?.id,
        logActivity: createActivity,
        repliedAll,
        replyToMessageId: replyEmail.messageId,
        subject: subject,
        tracking: true,
      };

      if (attachedFiles.length) {
        email.attachments = attachedFiles
          .filter((attachedFile) => attachedFile.file?.id)
          .map((attachedFile) => attachedFile.file!.id);
      }

      // for now, we are supporting only single case for activity creation:
      // when there is a single entity recipient
      // in future it plans to be extended with API improvements
      if (createActivity) {
        email.entityIds = [entity.id];
        email.associatedEntities = [];
        email.crmActivity = crmActivity ?? {
          name: replyEmail.subject,
          ...(emailActivityType
            ? { assigneeId: currentUserId, crmActivityType: { id: emailActivityType.id } }
            : {}),
        };
      } else {
        email.replyAddress = emailRecipients.reply;
        email.cc = emailRecipients.cc;
      }

      send({
        callback: (email) => {
          hideActivityCreationModal();
          displaySuccessNotification();
          onLoadSendEmail({
            messageId: email.messageId!,
            onFailure: () => {
              setSending(false);
              onClose(true);
            },
            onSuccess: () => {
              setSending(false);
              onClose(true);
            },
          });
        },
        email,
        failureCallback: (params) => {
          setSending(false);
          customFieldsValidationCallback?.(params);
        },
      });

      analytics.completed([repliedAll ? "Reply All" : "Reply", "Send Email"]);
    },
    [
      analytics,
      attachedFiles,
      createActivity,
      currentUserId,
      displaySuccessNotification,
      emailActivityType,
      body,
      emailRecipients,
      subject,
      entity,
      formLayout,
      hideActivityCreationModal,
      onClose,
      onLoadSendEmail,
      send,
      repliedAll,
      replyEmail,
      setSending,
    ]
  );

  const handleSendClick = useCallback(() => {
    if (openActivityModal && createActivity) {
      showActivityCreationModal();
    } else {
      handleSend();
    }
  }, [createActivity, handleSend, openActivityModal, showActivityCreationModal]);

  const handleCancel = useCallback(() => {
    onClose(false);
    analytics.cancelled(["Cancel"]);
  }, [analytics, onClose]);

  const allowCreateEmailActivity = useMemo(() => {
    return (
      emailRecipients.all.length === 1 &&
      normalizeEmail(emailRecipients.all[0].email) === normalizeEmail(entity.email)
    );
  }, [emailRecipients, entity]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FontAwesomeIcon className={styles.headerIcon} icon={repliedAll ? faReplyAll : faReply} />
        <Tooltip title={emailRecipientsHeader}>
          <div className={styles.headerRecipients}>{emailRecipientsHeader}</div>
        </Tooltip>
      </div>
      <EmailTemplateButton reply={true} />
      <CreateEmailBody
        action={
          <Space>
            <Button onClick={handleCancel} type="default">
              {intl.formatMessage(messages.cancelEmailButtonLabel)}
            </Button>
            <Tooltip title={sendEmailTooltip}>
              <Button
                disabled={sendEmailDisabled}
                loading={sending}
                onClick={handleSendClick}
                type="primary"
              >
                {intl.formatMessage(messages.sendEmailButtonLabel)}
              </Button>
            </Tooltip>
          </Space>
        }
        footer={
          allowCreateEmailActivity && (
            <Checkbox
              className={styles.createEmailActivity}
              onChange={setCreateActivity}
              value={createActivity}
            >
              {intl.formatMessage(messages.createEmailActivityLabel)}
            </Checkbox>
          )
        }
      />
      <PreviewModal />
      <ActivityCreationModal
        formLayout={formLayout}
        onBack={hideActivityCreationModal}
        onChangeFormLayout={setFormLayout}
        onCreate={handleSend}
        onHide={hideActivityCreationModal}
        open={activityCreationModalOpen}
        subject={replyEmail.subject}
      />
      <div ref={bottomRef} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUser(state)!.id,
  emailActivityType: getEmailActivityType(state)!,
  entity: getEmailThreadsEntity(state)!,
  openActivityModal: doesNeedToOpenActivityFormAfterSendingEmail(state),
});

const mapDispatchToProps = {
  onLoadSendEmail: loadSendEmail.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewEmail);
